/**
 * @fileoverview Hook Data Layer para mensajes de producto WhatsApp (ProductWsMessageMC).
 * @module useProductWsMessageData
 * @category Hook - Data Layer
 */
import { useMemo } from 'react';
import { formatText } from '../../../../util/textUtil';
import { checkForUrls } from '../../../../util/urlUtil';
import { isWhatsAppMsPlatform } from '../../../../services/inbox/inboxMsChatPlatform';

/**
 * Serializa precio de producto WS (almacenado en milésimas, igual que pedidos).
 * @param {number|string|null|undefined} price
 * @returns {string|null}
 */
function formatProductPrice(price) {
  if (price === null || price === undefined || price === '') return null;
  const numericPrice = Number(price);
  if (Number.isNaN(numericPrice)) return null;
  return `$ ${(numericPrice / 1000).toFixed(2)}`;
}

/**
 * Hook Data Layer para mensajes de producto WhatsApp.
 *
 * @param {Object} params
 * @param {string} params.platform
 * @param {Object} params.message
 * @returns {{ data: Object, productName: string, productDetailsHtml: string, captionHtml: string, url: string }}
 */
export default function useProductWsMessageData({ platform, message }) {
  return useMemo(() => {
    const product = message?.contentWsProduct;
    // Ver la nota de useOrderWsMessageData: el inbox MS manda el enum
    // canónico, así que la clave corta sola vaciaba la tarjeta del producto.
    const isWhatsappProduct = isWhatsAppMsPlatform(platform) && product;

    const url = isWhatsappProduct ? (product.url || '') : '';
    const productName = isWhatsappProduct ? (product.name || '') : '';
    const description = isWhatsappProduct ? (product.description || '') : '';
    const caption = isWhatsappProduct ? (product.text || '') : '';
    const formattedPrice = isWhatsappProduct ? formatProductPrice(product.price) : null;

    const detailParts = [];
    if (formattedPrice) detailParts.push(formattedPrice);
    if (description) detailParts.push(description);

    return {
      data: {
        ...message,
        contentMediaUrl: url || message?.contentMediaUrl
      },
      productName,
      productDetailsHtml: detailParts.join('<br />'),
      captionHtml: caption ? checkForUrls(formatText(caption)) : '',
      url
    };
  }, [platform, message]);
}
